import Vue from 'vue'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'

@Module({
  namespaced: true
})
export default class CustomersStore extends VuexModule {
  customerTypesLoaded = false
  customerTypes: KwixeoCustomers.CustomerTypes = []
  contactFunctionsLoaded = false
  contactFunctions: KwixeoCustomers.ContactFunctions = []
  originsLoaded = false
  origins: KwixeoCustomers.Origins = []
  activitiesLoaded = false
  activities: KwixeoCustomers.Activities = []

  // CLEAR STORE STATE
  @Action
  clearStore (): void {
    const elements = ['CustomerTypes', 'ContactFunctions', 'Origins', 'Activities']
    elements.forEach(key => {
      this.context.commit('set' + key, [])
      this.context.commit('set' + key + 'Loaded', false)
    })
  }

  // Sort a list alphabetically
  @Mutation
  orderList (param: { list: KwixeoInterface.GenericObject[]; key: string }): void {
    const key = param.key
    param.list.sort((a, b) => {
      const aKey = a[key].toLowerCase()
      const bKey = b[key].toLowerCase()
      if (aKey < bKey) return -1
      else if (aKey > bKey) return 1
      else return 0
    })
  }

  // Return the default customer type to use
  get defaultCustomerType (): KwixeoCustomers.CustomerType|undefined {
    return this.customerTypes.find(type => type.use_default)
  }

  // Change the loaded state
  @Mutation
  setCustomerTypesLoaded (state = true): void {
    this.customerTypesLoaded = state
  }

  // Set the customer types
  @Mutation
  setCustomerTypes (customerTypes: KwixeoCustomers.CustomerTypes): void {
    this.customerTypes = customerTypes
  }

  // Add or update a customer type
  @Mutation
  updateCustomerType (customerType: KwixeoCustomers.CustomerType): void {
    if (!this.customerTypesLoaded) {
      return
    }
    const index = this.customerTypes.findIndex(type => type.id === customerType.id)
    if (index >= 0) {
      Vue.set(this.customerTypes, index, customerType)
    } else {
      this.customerTypes.push(customerType)
    }
  }

  // Remove a customer type
  @Mutation
  removeCustomerType (customerType: KwixeoCustomers.CustomerType): void {
    if (!this.customerTypesLoaded) {
      return
    }
    const index = this.customerTypes.findIndex(type => type.id === customerType.id)
    if (index >= 0) {
      Vue.delete(this.customerTypes, index)
    }
  }

  // Load the customer types for server
  @Action
  async loadCustomerTypes (force = false): Promise<void> {
    if (!this.customerTypesLoaded || force) {
      const response = await Vue.prototype.$kwCustomers.axios.get('/customer/type?per_page=500&sort=label')
      await this.context.commit('setCustomerTypes', response.data.data)
      this.context.commit('setCustomerTypesLoaded')
    }
  }

  // Add or update a customer type
  @Action
  setCustomerType (customerType: KwixeoCustomers.CustomerType): void {
    this.context.commit('updateCustomerType', customerType)
    if (this.customerTypesLoaded) {
      this.context.commit('orderList', { list: this.customerTypes, key: 'label' })
    }
  }

  // Delete a customer type
  @Action
  deleteCustomerType (customerType: KwixeoCustomers.CustomerType): void {
    this.context.commit('removeCustomerType', customerType)
  }


  // Return the default contact function to use
  get defaultContactFunction (): KwixeoCustomers.ContactFunction|undefined {
    return this.contactFunctions.find(contactFunction => contactFunction.use_default)
  }

  // Change the loaded state
  @Mutation
  setContactFunctionsLoaded (state = true): void {
    this.contactFunctionsLoaded = state
  }

  // Set the contact functions
  @Mutation
  setContactFunctions (contactFunctions: KwixeoCustomers.ContactFunctions): void {
    this.contactFunctions = contactFunctions
  }

  // Add or update a contact function
  @Mutation
  updateContactFunction (contactFunction: KwixeoCustomers.ContactFunction): void {
    if (!this.contactFunctionsLoaded) {
      return
    }
    const index = this.contactFunctions.findIndex(type => type.id === contactFunction.id)
    if (index >= 0) {
      Vue.set(this.contactFunctions, index, contactFunction)
    } else {
      this.contactFunctions.push(contactFunction)
    }
  }

  // Remove a contact function
  @Mutation
  removeContactFunction (contactFunction: KwixeoCustomers.ContactFunction): void {
    if (!this.contactFunctionsLoaded) {
      return
    }
    const index = this.contactFunctions.findIndex(type => type.id === contactFunction.id)
    if (index >= 0) {
      Vue.delete(this.contactFunctions, index)
    }
  }

  // Load the contact functions for server
  @Action
  async loadContactFunctions (force = false): Promise<void> {
    if (!this.contactFunctionsLoaded || force) {
      const response = await Vue.prototype.$kwCustomers.axios.get('/customer/contact/function?per_page=500&sort=label')
      await this.context.commit('setContactFunctions', response.data.data)
      this.context.commit('setContactFunctionsLoaded')
    }
  }

  // Add or update a contact function
  @Action
  setContactFunction (contactFunction: KwixeoCustomers.ContactFunction): void {
    this.context.commit('updateContactFunction', contactFunction)
    if (this.contactFunctionsLoaded) {
      this.context.commit('orderList', { list: this.contactFunctions, key: 'label' })
    }
  }

  // Delete a contact function
  @Action
  deleteContactFunction (contactFunction: KwixeoCustomers.ContactFunction): void {
    this.context.commit('removeContactFunction', contactFunction)
  }


  // Return the default customer origin to use
  get defaultOrigin (): KwixeoCustomers.Origin|undefined {
    return this.origins.find(origin => origin.use_default)
  }

  // Change the loaded state
  @Mutation
  setOriginsLoaded (state = true): void {
    this.originsLoaded = state
  }

  // Set the customer origins
  @Mutation
  setOrigins (origins: KwixeoCustomers.Origins): void {
    this.origins = origins
  }

  // Add or update a customer origin
  @Mutation
  updateOrigin (origin: KwixeoCustomers.Origin): void {
    if (!this.originsLoaded) {
      return
    }
    const index = this.origins.findIndex(type => type.id === origin.id)
    if (index >= 0) {
      Vue.set(this.origins, index, origin)
    } else {
      this.origins.push(origin)
    }
  }

  // Remove a customer origin
  @Mutation
  removeOrigin (origin: KwixeoCustomers.Origin): void {
    if (!this.originsLoaded) {
      return
    }
    const index = this.origins.findIndex(type => type.id === origin.id)
    if (index >= 0) {
      Vue.delete(this.origins, index)
    }
  }

  // Load the customer origins for server
  @Action
  async loadOrigins (force = false): Promise<void> {
    if (!this.originsLoaded || force) {
      const response = await Vue.prototype.$kwCustomers.axios.get('/customer/origin?per_page=500&sort=label')
      await this.context.commit('setOrigins', response.data.data)
      this.context.commit('setOriginsLoaded')
    }
  }

  // Add or update a customer origin
  @Action
  setOrigin (origin: KwixeoCustomers.Origin): void {
    this.context.commit('updateOrigin', origin)
    if (this.originsLoaded) {
      this.context.commit('orderList', { list: this.origins, key: 'label' })
    }
  }

  // Delete a customer origin
  @Action
  deleteOrigin (origin: KwixeoCustomers.Origin): void {
    this.context.commit('removeOrigin', origin)
  }


  // Return the default customer activity to use
  get defaultActivity (): KwixeoCustomers.Activity|undefined {
    return this.activities.find(activity => activity.use_default)
  }

  // Change the loaded state
  @Mutation
  setActivitiesLoaded (state = true): void {
    this.activitiesLoaded = state
  }

  // Set the customer activities
  @Mutation
  setActivities (activities: KwixeoCustomers.Activities): void {
    this.activities = activities
  }

  // Add or update a customer activity
  @Mutation
  updateActivity (activity: KwixeoCustomers.Activity): void {
    if (!this.activitiesLoaded) {
      return
    }
    const index = this.activities.findIndex(type => type.id === activity.id)
    if (index >= 0) {
      Vue.set(this.activities, index, activity)
    } else {
      this.activities.push(activity)
    }
  }

  // Remove a customer activity
  @Mutation
  removeActivity (activity: KwixeoCustomers.Activity): void {
    if (!this.activitiesLoaded) {
      return
    }
    const index = this.activities.findIndex(type => type.id === activity.id)
    if (index >= 0) {
      Vue.delete(this.activities, index)
    }
  }

  // Load the customer activities for server
  @Action
  async loadActivities (force = false): Promise<void> {
    if (!this.activitiesLoaded || force) {
      const response = await Vue.prototype.$kwCustomers.axios.get('/customer/activity?per_page=500&sort=label')
      await this.context.commit('setActivities', response.data.data)
      this.context.commit('setActivitiesLoaded')
    }
  }

  // Add or update a customer activity
  @Action
  setActivity (activity: KwixeoCustomers.Activity): void {
    this.context.commit('updateActivity', activity)
    if (this.activitiesLoaded) {
      this.context.commit('orderList', { list: this.activities, key: 'label' })
    }
  }

  // Delete a customer activity
  @Action
  deleteActivity (activity: KwixeoCustomers.Activity): void {
    this.context.commit('removeActivity', activity)
  }
}
